/**
 * ShiptimizeCheckout — main orchestrator + map manager.
 *
 * Owns:
 *  - The ShiptimizeState instance (single source of truth)
 *  - The map interface (OpenMap or Gmaps)
 *  - Map display methods: openMap, closeMap, fetchPoints, geocodeAddress, …
 *  - Point selection (selectPoint) — bridges state and map
 *
 * Delegates to:
 *  - ShiptimizeClassicCheckout or ShiptimizeBlockCheckout for checkout-flow logic
 *  - ShiptimizeUI for all DOM manipulation (button, toast, modal)
 *  - ShiptimizeAjax for server calls
 *  - ShiptimizeState for all persistent state (carrier, pickup, address)
 *
 * Nothing in this file writes to window.carrier_id, #shipping_carrier_id,
 * localStorage pickup keys, or any form field directly.
 * All of that flows through ShiptimizeState.
 */
import ShiptimizeState from './state/shiptimize-state.js';
import ShiptimizeAjax    from './api/shiptimize-ajax.js';
import ShiptimizeUI      from './ui/shiptimize-ui.js';
import ShiptimizeClassicCheckout from './checkout/shiptimize-classic-checkout.js';
import ShiptimizeBlockCheckout   from './checkout/shiptimize-block-checkout.js';
import ShiptimizeOpenMap from './shiptimize-open-map.js';
import ShiptimizeGmaps   from './shiptimize-gmaps.js';

export default class ShiptimizeCheckout {

  // ── Constructor ─────────────────────────────────────────────────────────────

  /**
   * @param {object} options
   * @param {string} options.ajaxUrl          admin-ajax.php URL
   * @param {string} options.nonce            shiptimize_vars.nonce
   * @param {string} [options.host]
   * @param {string} [options.gmapsKey]       Google Maps API key (optional)
   * @param {string} [options.mapParentContainer]
   * @param {object} [options.labels]
   */
  constructor(options) {
    // ── Core dependencies ────────────────────────────────────────────────────
    this._state  = new ShiptimizeState(options.ajaxUrl, options.nonce);
    this._ajax   = new ShiptimizeAjax(options.ajaxUrl, options.nonce);
    this._ui     = new ShiptimizeUI();

    // ── Config ───────────────────────────────────────────────────────────────
    this._ajaxUrl            = options.ajaxUrl;
    this._gmapsKey           = options.gmapsKey || '';
    this._host               = options.host || 'https://shiptimize.me';
    this._mapParentContainer = options.mapParentContainer || 'body';
    this._labels             = options.labels || {};

    // ── Map state ────────────────────────────────────────────────────────────
    // _mapAddress is a working copy of the delivery address enriched with
    // geocoded Lat/Long. It is NOT the authoritative address — that lives in
    // ShiptimizeState. It is reset whenever the state address changes.
    this.mapinterface          = null;
    this._mapAddress           = {};
    this.pickupPoints          = [];
    this.cacheResults          = {};
    this.selectedDisplayOption = 0;
    this.searchRunning         = false;
    this.geocodingInProgress   = false;
    this.lastGeocodingRequest  = null;
    this.userScroll            = 0;
    this.queryResults          = [];
    this.timeoutKeyDown        = null;
    this.mapChanged            = null;
    this.currentSelectedRate   = null; // used by bpost geocoding logic

    // ── Weekday labels (provided by PHP via wp_localize_script) ─────────────
    this.weekdaynames = [
      typeof shiptimize_monday    !== 'undefined' ? shiptimize_monday    : 'Mon',
      typeof shiptimize_tuesday   !== 'undefined' ? shiptimize_tuesday   : 'Tue',
      typeof shiptimize_wednesday !== 'undefined' ? shiptimize_wednesday : 'Wed',
      typeof shiptimize_thursday  !== 'undefined' ? shiptimize_thursday  : 'Thu',
      typeof shiptimize_friday    !== 'undefined' ? shiptimize_friday    : 'Fri',
      typeof shiptimize_saturday  !== 'undefined' ? shiptimize_saturday  : 'Sat',
      typeof shiptimize_sunday    !== 'undefined' ? shiptimize_sunday    : 'Sun',
    ];

    // ── Bootstrap state from PHP-injected data ───────────────────────────────
    this._state.init(window.shiptimizeInitialState || {});

    // If PHP didn't determine the checkout type, detect it from the DOM
    if (!this._state.get('checkoutType') || this._state.get('checkoutType') === 'unknown') {
      this._state.set('checkoutType', this._detectCheckoutType());
    }

    // ── Create the appropriate checkout module ───────────────────────────────
    if (this._state.get('checkoutType') === 'classic') {
      this._checkout = new ShiptimizeClassicCheckout(
        this._state, this._ui, () => this.openMap(),
      );
    } else {
      this._checkout = new ShiptimizeBlockCheckout(
        this._state, this._ui, () => this.openMap(),
      );
    }

    // Expose globally for legacy compatibility
    window.shiptimizeWidget        = this;
    window.shiptimizePickupManager = this;

    this._injectDisabledButtonStyle();
  }

  // ── Public API ───────────────────────────────────────────────────────────────

  /**
   * Entry point. Called from the Shiptimize entry class after construction.
   */
  init() {
    // Seed _mapAddress from state (PHP-injected) or localStorage fallback
    const stateAddress = this._state.get('address');
    if (stateAddress?.Streetname1) {
      this._mapAddress = { ...stateAddress };
    } else {
      try {
        const stored = localStorage.getItem('ShiptimizeAddress');
        if (stored) this._mapAddress = JSON.parse(stored);
      } catch {}
    }

    // When state changes, sync derived values and reset _mapAddress if address changed
    this._state.onChange((snapshot) => {
      // Keep map interface carrier ID in sync when it changes
      if (snapshot.carrierId && this.mapinterface?.setCarrierId) {
        this.mapinterface.setCarrierId(snapshot.carrierId);
      }

      // Forward currentSelectedRate from block checkout (used by bpost geocoding)
      if (this._checkout?.currentSelectedRate !== undefined) {
        this.currentSelectedRate = this._checkout.currentSelectedRate;
      }

      if (!snapshot.address) return;
      const prev = this._mapAddress;
      const next = snapshot.address;

      const changed =
        prev?.PostalCode  !== next.PostalCode  ||
        prev?.Country     !== next.Country     ||
        prev?.City        !== next.City        ||
        prev?.Streetname1 !== next.Streetname1;

      if (changed) {
        // Clear cached results for the old address so we re-fetch on next openMap()
        if (prev?.Lat && prev?.Long) {
          delete this.cacheResults[this._hashLatLng({ lat: prev.Lat, lng: prev.Long })];
        }
        this._mapAddress = { ...next, Lat: '', Long: '' };
      }
    });

    this.loadScripts();
    this._checkout.init();
  }

  // ── Map ──────────────────────────────────────────────────────────────────────

  /**
   * Open the pickup point map modal.
   *
   * Address resolution order (stops at the first non-empty result):
   *  1. DOM extraction via _extractAddress() — works for standard WC templates
   *  2. PHP session via getCheckoutAddress() AJAX — works for Elementor, Avada,
   *     WPBakery, and any custom template whose field IDs differ from the WC defaults
   *  3. Whatever _mapAddress already holds from a previous successful extraction
   *
   * Once we have an address, geocode it (if not already cached) then fetch points.
   */
  openMap() {
    const cacheKey = this._hashLatLng({ lat: this._mapAddress?.Lat || 0, lng: this._mapAddress?.Long || 0 });

    if (!this.cacheResults[cacheKey]) {
      jQuery('body').append(`
        <div class="shiptimize-loader-btn" style="
          position:fixed!important;top:0!important;left:0!important;
          width:100%!important;height:100%!important;
          background:rgba(0,0,0,.7)!important;z-index:999999!important;
          display:flex!important;justify-content:center!important;align-items:center!important;">
          <div class="loader-squares">
            <div class="loader-square"></div>
            <div class="loader-square"></div>
            <div class="loader-square"></div>
          </div>
        </div>`);
    }

    // Refresh _mapAddress from state for both checkout types
    if (this._state.get('checkoutType') === 'classic') {
      this._checkout._extractAddress();
    }
    const fresh = this._state.get('address') || {};
    if (fresh.Streetname1) {
      this._mapAddress = {
        ...this._mapAddress,
        ...fresh,
        Country: (fresh.Country || this._mapAddress.Country || '').toUpperCase(),
      };
    }

    this.userScroll = jQuery('html,body').scrollTop();
    jQuery('html,body').scrollTop(0);
    jQuery('#sw').addClass('open');
    jQuery('#sw-map-wrapper').addClass('loading');
    this.selectDisplayOption(0);

    // If DOM extraction gave us an address, proceed immediately
    if (this._mapAddress.Streetname1) {
      jQuery('#sw-query').val(this._mapAddress.Streetname1);
      this._proceedWithOpenMap();
      return;
    }

    // DOM extraction returned empty — this is a custom template (Elementor, Avada, etc.)
    // Fall back to the server session, which always has the correct address.
    this._ajax.getCheckoutAddress()
      .then((resp) => {
        if (resp?.success && resp.data?.shipping_address) {
          const a = resp.data.shipping_address;
          this._mapAddress = {
            ...this._mapAddress,
            Streetname1: a.address_1 || '',
            Streetname2: a.address_2 || '',
            City:        a.city      || '',
            PostalCode:  a.postcode  || '',
            State:       a.state     || '',
            Country:     (a.country  || '').toUpperCase(),
          };
          // Push server address into state so subsequent calls don't need the round-trip
          this._state.set('address', { ...this._mapAddress });
        }
      })
      .catch(() => {
        // Server call failed — we'll proceed with whatever _mapAddress has
        // (could be a previous session value from localStorage)
      })
      .finally(() => {
        jQuery('#sw-query').val(this._mapAddress.Streetname1 || '');
        if (this._mapAddress.Streetname1) {
          this._proceedWithOpenMap();
        } else {
          // Truly no address anywhere — show a helpful message instead of hanging loader
          jQuery('.shiptimize-loader-btn').remove();
          this._ui.showAddressRequiredModal();
        }
      });
  }

  /**
   * Continue opening the map once we have a valid address in _mapAddress.
   * Geocodes if needed, then fetches pickup points.
   */
  _proceedWithOpenMap() {
    if (this._mapAddress.Lat && this._mapAddress.Long) {
      this._fetchPoints(this._mapAddress);
    } else {
      this._geocodeAddress(this._mapAddress, (geo) => {
        if (geo.length) geo = geo[0];
        this._mapAddress.Lat  = geo.lat;
        this._mapAddress.Long = geo.lng;
        if (geo.iso2 && !this._mapAddress.Country) {
          this._mapAddress.Country = geo.iso2;
        }
        this._fetchPoints(this._mapAddress);
      });
    }
  }

  closeMap() {
    jQuery('#sw').removeClass('open');
    jQuery('html,body').scrollTop(this.userScroll);
    jQuery('.shiptimize-loader-btn').stop(true, true).remove();
    jQuery('body, html').css('overflow', '');
  }

  /**
   * Called when the user selects a pickup point on the map.
   * Updates state (which syncs form fields + server session) and updates UI.
   *
   * @param {object} pickup  Pickup point data from the API
   * @returns {boolean}
   */
  selectPoint(pickup) {
    // Validate any extra required fields
    const eFieldInfo = this.selectedDisplayOption === 1
      ? jQuery('#sw-list-points')
      : jQuery('#sw-map-selected-point');

    if (typeof pickup.MapFieldsSelect !== 'undefined' && pickup.MapFieldsSelect.length > 0) {
      let valid = true;
      eFieldInfo
        .find('.shiptimize_mapfields' + this._escapeSelector(pickup.PointId))
        .each((idx, el) => {
          const field = jQuery(el);
          if (!field.val()) {
            alert(
              jQuery(jQuery('.shiptimize_mapfieldslabel' + this._escapeSelector(pickup.PointId)).get(idx)).text() +
              ': ' + (this._labels.mapfieldmandatory || 'required'),
            );
            valid = false;
          }
        });
      if (!valid) return false;
    }

    const label    = `${pickup.Information.Name} ${pickup.Information.Address}`;
    const extended = jQuery('.shiptimize_mapfields' + this._escapeSelector(pickup.PointId)).val() || '';

    // Extra OptionFields — store to localStorage for legacy consumers
    eFieldInfo
      .find('.shiptimize_mapfields' + this._escapeSelector(pickup.PointId))
      .each((_idx, el) => {
        const fieldId = jQuery(el).attr('data-id');
        localStorage.setItem(fieldId + 'val', jQuery(el).val());
      });

    // ── Single state update — drives everything else ───────────────────────
    this._state.set('pickupPoint', {
      id:       pickup.PointId,
      label:    label,
      extended: extended,
    });

    // localStorage: carrier validation on next page evaluate()
    localStorage.setItem('ShiptimizePointId',         pickup.PointId);
    localStorage.setItem('ShiptimizePointLabel',      pickup.Information.Name);
    localStorage.setItem('ShiptimizePickupCarrierId', String(this._state.get('carrierId') || ''));

    // ── UI updates ──────────────────────────────────────────────────────────
    this._ui.showPickupLabel(label);
    this._ui.enableButton();
    this._ui.dismissToast();
    jQuery('#pickup-required-notice').remove();
    jQuery('.shiptimize-toast').remove();

    this.closeMap();
    jQuery('#myModal').hide();

    // WC Blocks re-renders the Place Order button after map close.
    // Re-evaluate after the re-render so the button ends up enabled.
    if (this._checkout?.evaluate) {
      setTimeout(() => this._checkout.evaluate(), 350);
    }

    return true;
  }

  // ── Map HTML & scripts ───────────────────────────────────────────────────────

  addMapHtml() {
    const address   = this._mapAddress;
    const addresstr = address?.PostalCode || address?.Streetname1 || '';

    const mapHtml = `
      <div id="sw">
        <div id="sw__overlay"></div>
        <div id="sw__container">
          <div id="sw-search">
            <div id="sw-query-wrapper">
              <input type="text" id="sw-query" placeholder="${addresstr}">
            </div>
            <div id="sw-query-results"></div>
            <div id="query-options"></div>
          </div>
          <div id="sw-display-options"></div>
          <div id="sw-map-wrapper" class="sw-tab selected">
            <div class="sw-query-results-description"></div>
            <div id="sw-map" class="shiptimize-pickup__map"></div>
            <div id="sw-map-error"></div>
            <div id="sw-map-selected-point"></div>
          </div>
          <div class="sw-tab">
            <div class="sw-query-results-description"></div>
            <div id="sw-list-points"></div>
          </div>
          <div id="sw-map-message"></div>
          <div id="sw-search-status">
            <div class="sw-loader"><div></div><div></div><div></div></div>
          </div>
        </div>
      </div>`;

    jQuery(this._mapParentContainer).append(mapHtml);
    jQuery('.sw-loader div').addClass('shiptimize-background');

    const displayOptions = jQuery('#sw-display-options');
    const optMap  = jQuery(`<span class="sw-display-option selected">${typeof shiptimize_map  !== 'undefined' ? shiptimize_map  : 'Map'}</span>`);
    const optList = jQuery(`<span class="sw-display-option">${typeof shiptimize_list !== 'undefined' ? shiptimize_list : 'List'}</span>`);

    optMap.on('click',  () => this.selectDisplayOption(0));
    optList.on('click', () => this.selectDisplayOption(1));
    displayOptions.append(optMap, optList);

    const queryWrapper = jQuery('#sw-query-wrapper');
    const searchBtn    = jQuery(`<button id="sw-query-btn">${typeof shiptimize_search !== 'undefined' ? shiptimize_search : 'Search'}</button>`);
    queryWrapper.append(searchBtn);

    searchBtn.on('click', () => this.geocodeQuery(true));

    jQuery('#sw-query').on('keyup', (e) => {
      if (e.keyCode === 13) { this.geocodeQuery(); return; }
      clearTimeout(this.timeoutKeyDown);
      this.timeoutKeyDown = setTimeout(() => this.geocodeQuery(), 300);
    });

    jQuery('#sw__overlay').on('click', () => this.closeMap());
  }

  loadScripts() {
    if (typeof jQuery === 'undefined' || parseInt(jQuery.fn.jquery.substring(2, 2)) < 7) {
      this._loadScript('https://code.jquery.com/jquery-3.7.0.min.js', () => this._scriptsLoaded());
    } else {
      this._scriptsLoaded();
    }
  }

  // ── Map navigation ───────────────────────────────────────────────────────────

  selectDisplayOption(idx) {
    this.selectedDisplayOption = idx;
    const options = jQuery('.sw-display-option, .sw-tab');
    options.removeClass('selected');
    jQuery(options.get(idx)).addClass('selected');
    jQuery(jQuery('.sw-tab').get(idx)).addClass('selected');

    if (idx === 0 && this.mapinterface?.pickupPoints?.length > 0) {
      this.mapinterface.fitBounds();
    }
  }

  centerMap(lat, lng) {
    this.mapinterface.centerMap(lat, lng);
  }

  mapMoved(mapcenter) {
    jQuery('#sw-point-info').html('');
    return new Promise((resolve) => {
      this._mapAddress.Lat  = mapcenter.lat;
      this._mapAddress.Long = mapcenter.lng;
      this._fetchPoints(this._mapAddress, resolve);
    });
  }

  // ── Geocoding ────────────────────────────────────────────────────────────────

  geocodeQuery(isButtonClick = false) {
    jQuery('#sw-query-results').html('');

    const queryVal = jQuery('#sw-query').val();
    this._mapAddress.Lat  = null;
    this._mapAddress.Long = null;

    if (queryVal.length < 4) return;

    // Country from form (block checkout has different selectors)
    const countrySelectors = [
      '#components-form-token-input-0',
      '#shipping-country',
      '#billing-country',
      '#select2-billing_country-container',
    ];
    for (const sel of countrySelectors) {
      const el = jQuery(sel);
      if (el.length && el.val()) {
        this._mapAddress.Country = el.val();
        break;
      }
    }

    if (
      typeof SHIPTIMIZE_PLUGIN_URL !== 'undefined' &&
      SHIPTIMIZE_PLUGIN_URL.includes('bpost') &&
      isButtonClick
    ) {
      const country = this._bpostCountry();
      this.mapinterface.geocodeBpost({ address: queryVal, country }, (resp) => this._displayPlaces(resp));
    } else {
      this.mapinterface.geocode({ address: queryVal }, (resp) => this._displayPlaces(resp));
    }
  }

  // ── Points display ───────────────────────────────────────────────────────────

  displayResults(data) {
    this.mapinterface.clearMarkers();
    jQuery('#sw__container').removeClass('searching');
    this._stopLoadingIndicator();

    jQuery('.sw-query-results-description').html(
      `<div class="sw-query-results-description">${typeof shiptimize_the !== 'undefined' ? shiptimize_the : ''} ${data.Count} ${typeof shiptimize_closest !== 'undefined' ? shiptimize_closest : ''}</div>`,
    );

    setTimeout(() => {
      this.pickupPoints = data.Point;
      this.mapChanged   = Date.now();

      this._updateList(this.pickupPoints);
      this.mapinterface.addMarkers(this.pickupPoints, (idx) => {
        const parent = jQuery('#sw-map-selected-point');
        parent.html('');
        this._addPointInfo(this.pickupPoints[idx], false, '', parent);
        this.mapinterface.selectPoint(idx);
      });

      setTimeout(() => {
        jQuery('.shiptimize-loader-btn').fadeOut(300, function () { jQuery(this).remove(); });
      }, 300);
    }, 100);
  }

  displayMessage(msg) {
    jQuery('#sw-map-message').addClass('open');
    jQuery('#sw-map-message').html(
      msg?.Id === 99
        ? (typeof shiptimize_no_points_found !== 'undefined' ? shiptimize_no_points_found : 'No pickup points found.')
        : msg,
    );
  }

  // ── Private: points ──────────────────────────────────────────────────────────

  _fetchPoints(address, onDone) {
    const carrierId = this._state.get('carrierId');

    if (!carrierId) {
      console.error('ShiptimizeCheckout._fetchPoints: carrierId is not set');
      this.displayMessage('⚠️ Shipping method not properly selected. Please refresh the page.');
      jQuery('.shiptimize-loader-btn').remove();
      return;
    }

    if (address.Country) address.Country = address.Country.toUpperCase();

    this._state.set('uiStatus', 'loading');
    jQuery('#sw-map-selected-point').html('');
    jQuery('#sw-map-message').removeClass('open');
    jQuery('.sw-query-results-description').html('');

    if (!this.mapinterface.isMapMoving()) {
      jQuery('#sw__container').addClass('searching');
    }

    // Check cache before hitting the API
    const cacheKey = this._hashLatLng({ lat: address.Lat, lng: address.Long });
    if (this.cacheResults[cacheKey]) {
      this.displayResults(this.cacheResults[cacheKey]);
      return;
    }

    jQuery
      .getJSON(this._ajaxUrl, {
        Address:   address,
        CarrierId: carrierId,
        action:    'shiptimize_pickup_locations',
      })
      .done((data) => {
        this.mapinterface.clearMarkers();
        this._stopLoadingIndicator();
        jQuery('#sw-map-wrapper').removeClass('loading');
        this.searchRunning = false;
        jQuery('#sw__container').removeClass('searching');

        if (data.Error && data.Error.Id !== 0) {
          const info = data.Error.Info?.toLowerCase() || '';
          let msg = '⚠️ Something went wrong while searching for pickup points. Please verify your address or try again.';
          if (info.includes('invalid') || info.includes('not found')) {
            msg = "⚠️ Please check your address — we couldn't find any pickup points near that location.";
          } else if (info.includes('timeout')) {
            msg = '⚠️ The request took too long. Please try again in a moment.';
          }
          this.displayMessage(msg);
          return;
        }

        if (data.Point?.length > 0) {
          this.cacheResults[cacheKey] = data;
          this.displayResults(data);
        } else {
          this.displayMessage('⚠️ No pickup points found for the provided address.');
        }

        if (onDone) setTimeout(onDone, 300);
      })
      .fail(() => {
        this.displayMessage(typeof shiptimize_no_points_found !== 'undefined' ? shiptimize_no_points_found : 'No pickup points found.');
      });
  }

  _displayPlaces(places) {
    jQuery('.sw-query-results-description').html('');
    this.queryResults = places;

    let html = '';
    places.forEach((place, i) => {
      if (typeof place.address?.PostalCode !== 'undefined') {
        html += `<div class="sw-query-result" data-idx="${i}">${place.display_name}</div>`;
      }
    });

    jQuery('#sw-query-results').html(html || (typeof shiptimize_no_results !== 'undefined' ? shiptimize_no_results : 'No results'));

    jQuery('.sw-query-result').on('click', (e) => {
      const idx   = parseInt(jQuery(e.target).attr('data-idx'));
      const place = this.queryResults[idx];
      if (!place) return;

      this._mapAddress.Lat  = place.lat;
      this._mapAddress.Long = place.lng;

      jQuery('#sw-query-results').html('');
      jQuery('#sw-query').val(place.display_name);

      if (place.address) {
        Object.assign(this._mapAddress, place.address);
        this._mapAddress.Streetname1 = place.address.Streetname1;
      }

      this._fetchPoints(this._mapAddress);
    });
  }

  _geocodeAddress(address, callback) {
    if (this.geocodingInProgress) {
      if (this.lastGeocodingRequest?.abort) {
        this.lastGeocodingRequest.abort();
      }
    }
    this.geocodingInProgress = true;

    const wrap = (_fn) => (result) => {
      this.geocodingInProgress  = false;
      this.lastGeocodingRequest = null;
      callback(result);
    };

    // bpost: use Belgium-specific geocoding
    if (
      typeof SHIPTIMIZE_PLUGIN_URL !== 'undefined' &&
      SHIPTIMIZE_PLUGIN_URL.includes('bpost') &&
      (address.Country === 'BE' || address.Country === 'be' || address.Country === 'Belgium')
    ) {
      this._ajax.getCheckoutAddress()
        .then((resp) => {
          let finalAddress = address;

          if (resp?.success && resp.data?.shipping_address) {
            const backend = resp.data.shipping_address;
            const changed =
              address.City       !== backend.city     ||
              address.PostalCode !== backend.postcode ||
              address.Streetname1 !== backend.address_1;

            if (changed) {
              finalAddress = {
                Country:     backend.country,
                City:        backend.city,
                PostalCode:  backend.postcode,
                Streetname1: backend.address_1,
                Streetname2: backend.address_2,
                State:       backend.state,
              };
              Object.assign(this._mapAddress, finalAddress);
            }
          }

          this._proceedWithBpostGeocoding(finalAddress, wrap(callback));
        })
        .catch(() => {
          this._proceedWithBpostGeocoding(address, wrap(callback));
        });

    } else {
      // Geocoding strategy — three tiers, each narrower than the last:
      //
      //  1. Full address  (street + postal + city + country) — most precise
      //  2. Postal + country                                 — very reliable for EU
      //  3. City  + country                                  — last resort
      //
      // Nominatim rate-limits to 1 req/s and sometimes returns nothing for a
      // combined street+postal query even when the address is valid. Falling
      // through to postal-only before city-only fixes the "please verify your
      // address" false alarm that users see with correct addresses.

      const tryFullAddress = (onDone) => {
        this.mapinterface.geocodeAddressParts(
          (geo) => {
            if (geo.lat && geo.lng) { onDone(geo); return; }
            tryPostalCode(onDone);
          },
          address.City, address.Country, address.PostalCode, address.Streetname1,
        );
      };

      const tryPostalCode = (onDone) => {
        if (!address.PostalCode) { tryCity(onDone); return; }
        this.mapinterface.geocodeAddressParts(
          (geo) => {
            if (geo.lat && geo.lng) { onDone(geo); return; }
            tryCity(onDone);
          },
          '', address.Country, address.PostalCode, '',
        );
      };

      const tryCity = (onDone) => {
        if (!address.City) { onDone({ iso2: address.Country, lat: '', lng: '' }); return; }
        this.mapinterface.geocodeAddressParts(
          (geo) => {
            // If city-level also fails, fall back to a country-centre coordinate
            // so Leaflet still has valid coordinates to render the map tiles.
            if (!geo.lat || !geo.lng) {
              const countryDefaults = {
                PT: { lat: '39.5', lng: '-8.0' },
                BE: { lat: '50.5', lng: '4.5'  },
                NL: { lat: '52.1', lng: '5.3'  },
                DE: { lat: '51.2', lng: '10.4' },
                DK: { lat: '56.0', lng: '10.0' },
                FR: { lat: '46.6', lng: '2.3'  },
                ES: { lat: '40.4', lng: '-3.7' },
              };
              const fallback = countryDefaults[address.Country?.toUpperCase()] || {};
              onDone({ iso2: address.Country, lat: fallback.lat || '', lng: fallback.lng || '' });
            } else {
              onDone(geo);
            }
          },
          address.City, address.Country,
        );
      };

      tryFullAddress((geo) => {
        if (!geo.lat || !geo.lng) {
          // All three tiers failed. The map is already open — show a soft
          // prompt in the search bar area so the customer can search manually.
          // Do NOT say "verify your address" — the address may be correct but
          // simply outside Nominatim's coverage, or rate-limited.
          this.displayMessage(
            '📍 We couldn\'t pin your address automatically. Use the search bar above to find nearby pickup points.',
          );
          jQuery('.shiptimize-loader-btn').remove();
        }
        wrap(callback)([geo]);
      });
    }
  }

  _proceedWithBpostGeocoding(address, callback) {
    const country = this._bpostCountry(address);

    this.mapinterface.geocodeAddressPartsBpost(
      (geocode) => {
        if (!geocode.lat) {
          return this.mapinterface.geocodeAddressPartsBpost(
            (geo2) => callback(geo2),
            address.City, country,
          );
        }
        callback(geocode);
      },
      address.City, country, address.PostalCode, address.Streetname1,
    );
  }

  _bpostCountry(address) {
    address = address || this._mapAddress;
    if (!this.currentSelectedRate) return address?.Country || 'BE';

    const isIntl = this._isInternationalCarrier(this.currentSelectedRate);
    if (isIntl === false) return 'BE';
    if (isIntl === true)  return address?.Country || 'BE';
    return address?.Country || 'BE';
  }

  // ── Private: UI helpers ──────────────────────────────────────────────────────

  _updateList(points) {
    jQuery('#sw-list-points').html('');
    const parent = jQuery('#sw-list-points');
    points.forEach((p) => this._addPointInfo(p, false, '', parent));
  }

  /**
   * Render a pickup point card inside a map container.
   */
  _addPointInfo(p, selected, extraClass, parentContainer) {
    const local    = p.Information.Address;
    const openHtml = this._getWorkingDays(p.WorkingHours);

    const card = jQuery(`
      <div class="sw-point-info ${extraClass || ''}">
        <h4 class="sw-point-info-name">${p.Information.Name}</h4>
        <div class="sw-point-info-addr">${local}</div>
        ${p.Distance !== null
          ? `<div class="sw-point-info-distance">${typeof shiptimize_distance !== 'undefined' ? shiptimize_distance : ''} ${p.Distance} ${typeof shiptimize_meter !== 'undefined' ? shiptimize_meter : ''}</div>`
          : ''}
        <div class="sw-point-info-open">${openHtml}</div>
      </div>`);

    if (typeof p.MapFieldsSelect !== 'undefined') {
      p.MapFieldsSelect.forEach((field) => {
        card.append(`<div class="sw-point-info-additional"><label>${field}</label><input data-id="${field}" class="shiptimize_mapfields${p.PointId}" type="text" id="${field}${p.PointId}"/></div>`);
      });
    }

    const btn = jQuery(`<button class="sw-point-info-btn ${selected ? 'selected' : ''}">${selected ? (typeof shiptimize_selected !== 'undefined' ? shiptimize_selected : 'Selected') : (typeof shiptimize_select !== 'undefined' ? shiptimize_select : 'Select')}</button>`);
    btn.on('click', () => this.selectPoint(p));
    card.append(btn);
    parentContainer.append(card);
  }

  _stopLoadingIndicator() {
    jQuery('#sw-map-wrapper').removeClass('loading');
    this.searchRunning = false;
  }

  // ── Private: carrier & rate helpers ─────────────────────────────────────────

  _isInternationalCarrier(rate) {
    if (!rate?.meta_data) return null;
    const meta = rate.meta_data.find((m) => m.key === 'is_international');
    if (!meta) return null;
    return meta.value === true || meta.value === '1' || meta.value === 1;
  }

  // ── Private: script loading ──────────────────────────────────────────────────

  _scriptsLoaded() {
    this.eSearchStatus = jQuery('#search-status');
    this.addMapHtml();

    this.mapinterface = this._gmapsKey
      ? new ShiptimizeGmaps({ gmapskey: this._gmapsKey, address: this._mapAddress }, this)
      : new ShiptimizeOpenMap({ address: this._mapAddress }, this);

    this.mapinterface.initMap();
    this.mapinterface.addMapMoveListener((center) => this.mapMoved(center));
  }

  _loadScript(url, callback) {
    const script     = document.createElement('script');
    script.type      = 'text/javascript';
    script.onload    = () => callback && callback();
    script.src       = url;
    document.getElementsByTagName('head')[0].appendChild(script);
  }

  static loadStyle(url) {
    const link  = document.createElement('link');
    link.rel    = 'stylesheet';
    link.href   = url;
    document.getElementsByTagName('head')[0].appendChild(link);
  }

  // ── Private: working hours ───────────────────────────────────────────────────

  _getWorkingDays(workingHours) {
    if (!workingHours) return '';

    const dayGroups = [];
    for (let d = 0; d < 7; d++) {
      if (!workingHours[d]) continue;
      const hours    = this._formatWorkingHours(workingHours[d]);
      const last     = dayGroups[dayGroups.length - 1];
      if (last && last.hours === hours && last.end + 1 === d) {
        last.end = d;
      } else {
        dayGroups.push({ start: d, end: d, hours });
      }
    }

    return dayGroups
      .filter((g) => g.hours.match(/\d+/))
      .map((g) => {
        const range = g.start === g.end
          ? this.weekdaynames[g.start]
          : `${this.weekdaynames[g.start]} - ${this.weekdaynames[g.end]}`;
        return `<div class="sw-point-info-day" style="margin-bottom:-10px"><label>${range}</label>: ${g.hours}</div>`;
      })
      .join('');
  }

  _formatWorkingHours(intervals) {
    return intervals
      .map((h) => (h.OpenTime && h.CloseTime ? `${h.OpenTime} - ${h.CloseTime}` : ''))
      .filter(Boolean)
      .join(' | ');
  }

  // ── Private: misc ─────────────────────────────────────────────────────────────

  _detectCheckoutType() {
    if (
      jQuery('.wc-block-checkout').length > 0 ||
      jQuery("[data-block-name='woocommerce/checkout']").length > 0 ||
      (window.wp?.data && window.wp.data.select('wc/store/cart'))
    ) return 'block';

    if (
      jQuery('form.checkout').length > 0 ||
      jQuery('.woocommerce-checkout').length > 0 ||
      jQuery('#place_order').length > 0
    ) return 'classic';

    return 'unknown';
  }

  _hashLatLng(point) {
    const lat = (String(point.lat || '')).replace('.', '-');
    const lng = (String(point.lng || '')).replace('.', '-');
    return `r${lat}_${lng}`;
  }

  _escapeSelector(str) {
    return str.replace(/([!"#$%&'()*+,.\/:;<=>?@[\\\]^`{|}~])/g, '\\$1');
  }

  addCustomStyle(css) {
    const style = document.createElement('style');
    style.type  = 'text/css';
    style.appendChild(document.createTextNode(css));
    document.getElementsByTagName('head')[0].appendChild(style);
  }

  _injectDisabledButtonStyle() {
    this.addCustomStyle(`
      button.shiptimize-disabled,
      button.shiptimize-disabled:hover,
      button.shiptimize-disabled:active {
        pointer-events: none !important;
        cursor: not-allowed !important;
        background-color: grey !important;
        opacity: 0.5 !important;
      }
    `);
  }
}
